import { InfiniteLine } from "@faro-lotv/lotv";
import {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Vector3 } from "three";

/** Reference to the detected features */
export type FeatureDetectionRef = {
  /** Reference to the detected normal */
  normal: RefObject<Vector3 | undefined | null>;

  /**
   * Reference to the detected edge. The edge is represented as an infinite line
   * with a point and a direction. The point is the projection of the given position
   * on the line.
   */
  edge: RefObject<InfiniteLine | undefined | null>;

  /** Reference to the detected corner */
  corner: RefObject<Vector3 | undefined | null>;
};

type FeatureDetectionContextData = {
  /** Flag to enable or disable normal detection */
  normalDetection: boolean;

  /** The setter for normal detection flag */
  setNormalDetection(show: boolean): void;

  /** Flag to enable or disable edge cornere detection */
  edgeCornerDetection: boolean;

  /** The setter for edge corner detection flag */
  setEdgeCornerDetection(show: boolean): void;

  /** The setter for the geometric features detected */
  setFeatures(normal?: Vector3, edge?: InfiniteLine, corner?: Vector3): void;

  /** Reference to detected feature objects */
  features: FeatureDetectionRef;
};

const FeatureDetectionContext = createContext<
  FeatureDetectionContextData | undefined
>(undefined);

/**
 * @returns a context used by feature detection of the scene
 */
export function useFeatureDetectionContext(): FeatureDetectionContextData {
  const context = useContext(FeatureDetectionContext);
  if (!context) {
    throw new Error("FeatureDetectionContext is not initialized.");
  }
  return context;
}

/**
 * @returns a component to keep the feature detection data of the scene
 */
export function FeatureDetectionContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [normalDetection, setNormalDetection] = useState(false);
  const [edgeCornerDetection, setEdgeCornerDetection] = useState(false);

  const normal = useRef<Vector3 | undefined>(undefined);
  const edge = useRef<InfiniteLine | undefined>(undefined);
  const corner = useRef<Vector3 | undefined>(undefined);

  useEffect(() => {
    if (!normalDetection) {
      normal.current = undefined;
      edge.current = undefined;
      corner.current = undefined;
    }
  }, [normalDetection]);

  useEffect(() => {
    if (!edgeCornerDetection) {
      edge.current = undefined;
      corner.current = undefined;
    }
  }, [edgeCornerDetection]);

  const setFeatures = useCallback<FeatureDetectionContextData["setFeatures"]>(
    (newNormal, newEdge, newCorner) => {
      if (newNormal) {
        if (normal.current === undefined) {
          normal.current = new Vector3();
        }
        normal.current.copy(newNormal);
      } else {
        normal.current = undefined;
      }

      if (newEdge) {
        if (edge.current === undefined) {
          edge.current = {
            point: new Vector3(),
            direction: new Vector3(),
          };
        }
        edge.current.direction.copy(newEdge.direction);
        edge.current.point.copy(newEdge.point);
      } else {
        edge.current = undefined;
      }

      if (newCorner) {
        if (corner.current === undefined) {
          corner.current = new Vector3();
        }
        corner.current.copy(newCorner);
      } else {
        corner.current = undefined;
      }
    },
    [],
  );

  const value = useMemo<FeatureDetectionContextData>(
    () => ({
      normalDetection,
      setNormalDetection,
      edgeCornerDetection,
      setEdgeCornerDetection,
      setFeatures,
      features: {
        normal,
        edge,
        corner,
      },
    }),
    [normalDetection, edgeCornerDetection, setFeatures, normal, edge, corner],
  );

  return (
    <FeatureDetectionContext value={value}>{children}</FeatureDetectionContext>
  );
}
